import type {
  BaseEquipmentItem,
  EquipmentCatalog,
  EquipmentLayer,
  EquipmentUiSkinConfig,
  NicknameSkinEquipmentItem,
  SpeechBubbleEquipmentItem,
} from '@zep/types/play/equipment';

export type EquipmentAssetPathResolver = (path: string) => string;

export function resolveEquipmentItemAssetPaths<T extends BaseEquipmentItem>(
  item: T,
  resolvePath: EquipmentAssetPathResolver,
): T {
  return {
    ...item,
    thumbnailPath: resolvePath(item.thumbnailPath),
    layers: (item.layers ?? []).map(layer =>
      resolveEquipmentLayerAssetPaths(layer, resolvePath),
    ),
  };
}

export function resolveEquipmentCatalogAssetPaths(
  catalog: EquipmentCatalog,
  resolvePath: EquipmentAssetPathResolver,
): EquipmentCatalog {
  return {
    attack: catalog.attack.map(item =>
      resolveEquipmentItemAssetPaths(item, resolvePath),
    ),
    effect: catalog.effect.map(item =>
      resolveEquipmentItemAssetPaths(item, resolvePath),
    ),
    ride: catalog.ride.map(item =>
      resolveEquipmentItemAssetPaths(item, resolvePath),
    ),
    chair: catalog.chair?.map(item =>
      resolveEquipmentItemAssetPaths(item, resolvePath),
    ),
    pet: catalog.pet?.map(item =>
      resolveEquipmentItemAssetPaths(item, resolvePath),
    ),
    speech_bubble: catalog.speech_bubble?.map(item =>
      resolveUiEquipmentItemAssetPaths(item, resolvePath),
    ),
    nickname_skin: catalog.nickname_skin?.map(item =>
      resolveUiEquipmentItemAssetPaths(item, resolvePath),
    ),
  } satisfies EquipmentCatalog & Record<keyof EquipmentCatalog, unknown>;
}

function resolveEquipmentLayerAssetPaths(
  layer: EquipmentLayer,
  resolvePath: EquipmentAssetPathResolver,
): EquipmentLayer {
  const { body } = layer.resource;
  if (!body) {
    return layer;
  }

  return {
    ...layer,
    resource: {
      ...layer.resource,
      body: resolvePath(body),
    },
  };
}

function resolveUiEquipmentItemAssetPaths<
  T extends SpeechBubbleEquipmentItem | NicknameSkinEquipmentItem,
>(item: T, resolvePath: EquipmentAssetPathResolver): T {
  return {
    ...resolveEquipmentItemAssetPaths(item, resolvePath),
    uiSkinConfig: resolveEquipmentUiSkinConfigAssetPaths(
      item.uiSkinConfig,
      resolvePath,
    ),
  };
}

function resolveEquipmentUiSkinConfigAssetPaths(
  config: EquipmentUiSkinConfig,
  resolvePath: EquipmentAssetPathResolver,
): EquipmentUiSkinConfig {
  return {
    ...config,
    spriteImage: resolveEquipmentUiSkinImageAssetPath(
      config.spriteImage,
      resolvePath,
    ),
    tailImage: resolveEquipmentUiSkinImageAssetPath(
      config.tailImage,
      resolvePath,
    ),
  };
}

function resolveEquipmentUiSkinImageAssetPath(
  image: EquipmentUiSkinConfig['spriteImage'],
  resolvePath: EquipmentAssetPathResolver,
): EquipmentUiSkinConfig['spriteImage'] {
  if (!image?.url) {
    return null;
  }

  return {
    ...image,
    url: resolvePath(image.url),
    version: image.version ?? 1,
  };
}
